import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSettings, selectUser } from "../store/selector";
import { parseValues } from "./getSetting";

/**
 * Shared hook that determines whether the current user is authorized
 * to access the Portal Credentials feature.
 *
 * Authorization requires:
 * 1. The "portal-credentials.enabled" setting to be `true`.
 * 2. A non-empty "portal-credentials.whitelisted-usernames" list.
 * 3. The current user's email to appear in that whitelist (case-insensitive).
 */
const usePortalCredentialsEnabled = () => {
  const allSettings = useSelector(selectSettings);
  const user = useSelector(selectUser);

  return useMemo(() => {
    // Check if feature flag is enabled
    const enabledSetting = allSettings["portal-credentials.enabled"];
    if (!enabledSetting || parseValues(enabledSetting) !== true) {
      return false;
    }

    // Check if user is in the whitelist
    const whitelistSetting = allSettings["portal-credentials.whitelisted-usernames"];
    if (!whitelistSetting) {
      return false;
    }

    let whitelist;
    try {
      whitelist = parseValues(whitelistSetting);
    } catch {
      return false;
    }
    if (!Array.isArray(whitelist) || whitelist.length === 0) {
      return false;
    }

    // Case-insensitive username (email) comparison
    const userEmail = user?.email?.toLowerCase();
    if (!userEmail) {
      return false;
    }

    return whitelist.some((whitelistedUser) => whitelistedUser && whitelistedUser.toLowerCase() === userEmail);
  }, [allSettings, user?.email]);
};

export { usePortalCredentialsEnabled };
